/**
 * Whole-footprint warning-zone click recovery (#474, W1 Slice 2).
 *
 * The four coloured warning-zone layers (CAP extreme-weather, power outages, road
 * closures, estimated comms degradation) render big ground-clamped / height-0 FILLS
 * the operator must be able to click ANYWHERE inside to open. But MapView resolves a
 * click with a single topmost `scene.pick`, which at a given pixel can land on a
 * decorative province boundary / city label — or the bare globe — sitting in front
 * of (or instead of) the zone fill, so the click resolves to nothing selectable and
 * the panel never opens.
 *
 * This module is the pure, Cesium-free seam MapView uses to RECOVER the zone: after
 * every higher-priority point-feature check has missed on the topmost pick, walk the
 * drillPick stack (front-to-back) and return the FIRST result any zone layer claims.
 * It runs LAST — only in the "nothing selectable on top" branch, right before the
 * deselect — so it never steals a click from a marker: a marker floats
 * (disableDepthTest) and would itself have BEEN the topmost pick; this only rescues
 * an otherwise-dead click on bare zone fill.
 *
 * Kept Cesium-free (operates on the `{ id: <entity> }` shape a picked Cesium object
 * exposes, with injected `claims` predicates) so the arbitration is unit-testable
 * without a WebGL viewer — the same pattern as coveragePick.
 */

/** A picked / drilled Cesium object exposes its Entity as `.id`. */
interface PickedLike {
  id?: unknown;
}

/**
 * Walk a drillPick stack (front-to-back) and return the FIRST result whose entity
 * any of the `claims` predicates recognises — the frontmost zone feature under the
 * cursor — or null if none. Each claim receives the picked object's `.id` (the
 * Cesium Entity a layer's `pick()` maps back to its feature). Generic so MapView
 * keeps the exact type `scene.drillPick` returns.
 */
export function firstClaimedZonePick<T extends PickedLike>(
  drilled: ReadonlyArray<T>,
  claims: ReadonlyArray<(entity: unknown) => boolean>,
): T | null {
  for (const hit of drilled) {
    if (!hit) continue;
    const entity = hit.id;
    if (entity != null && claims.some((claim) => claim(entity))) return hit;
  }
  return null;
}
